import React, { useEffect, useRef } from 'react';
import { useLocation } from 'react-router-dom';
import '../styles/ScrollRail.css';

// Responsive constants
const getResponsiveConstants = () => {
  const isMobile = window.innerWidth <= 768;
  const isTablet = window.innerWidth <= 1024;
  
  return {
    STICKY_TOP: isMobile ? 100 : isTablet ? 120 : 140,
    GAP_BELOW_HERO: isMobile ? 40 : isTablet ? 60 : 80
  };
};

// Calculate rail position based on content container
const getRailPosition = () => {
  const contentMaxWidth = 1200; // max-width of content containers
  const contentPadding = 32; // 2rem = 32px padding on each side
  
  // Responsive rail offset based on screen size
  const isMobile = window.innerWidth <= 768;
  const isTablet = window.innerWidth <= 1024;
  const isLargeScreen = window.innerWidth > 1700;
  
  // Distance from content edge - increased for screens larger than 1550px
  const railOffset = isMobile ? 30 : isTablet ? 60 : isLargeScreen ? 190 : 90;
  
  // Calculate the left edge of the content area
  const viewportWidth = window.innerWidth;
  const contentWidth = Math.min(contentMaxWidth, viewportWidth - (contentPadding * 2));
  const contentLeft = (viewportWidth - contentWidth) / 2;
  
  // Position rail to the left of content with offset
  const railLeft = contentLeft - railOffset;
  
  // Ensure minimum distance from viewport edge
  const minDistance = isMobile ? 10 : 20;
  return Math.max(minDistance, railLeft);
};
// Check routes with hero image
const ROUTES_WITH_HERO = new Set<string>([
  '/', '/unternehmen', '/referenzen', '/investmentansatz', '/project-tu', '/project-sm', '/project-ko', '/project-je', '/project-mb91'
]);

// Find Hero-Box
function getHeroBottomAbs(): number | null {
  const hero = document.querySelector(
    '.header-section, .hero, .page-hero, .homepage-hero, .company-header, .project-tu-header, .projects-sm-header, .references-header-section, .investment-header-section'
  ) as HTMLElement | null;
  if (!hero) return null;
  const rect = hero.getBoundingClientRect();
  return rect.bottom + window.scrollY; // absolute document-coordinates
}

const ScrollRail: React.FC = () => {
  const circleRef = useRef<HTMLDivElement>(null);
  const lineRef = useRef<HTMLDivElement>(null);
  const { pathname } = useLocation();

  useEffect(() => {
    const onScrollOrResize = () => {
      const circle = circleRef.current;
      const line = lineRef.current;
      if (!circle || !line) return;

      // 1) Calculate rail position based on content container
      const railLeft = getRailPosition();
      circle.style.left = `${railLeft}px`;
      line.style.left = `${railLeft}px`;

      // 2) Determine target top (viewport coordinate)
      const { STICKY_TOP, GAP_BELOW_HERO } = getResponsiveConstants();
      let desiredTop = STICKY_TOP;

      if (ROUTES_WITH_HERO.has(pathname)) {
        const heroBottomAbs = getHeroBottomAbs();
        if (heroBottomAbs !== null) {
          // Distance from the hero bottom edge to the viewport top edge
          const heroBottomViewport = heroBottomAbs - window.scrollY;
          // Circle follows the hero bottom edge (plus gap) as long as it is higher than STICKY_TOP
          desiredTop = Math.max(STICKY_TOP, heroBottomViewport + GAP_BELOW_HERO);
        }
      }

      // 3) Position circle
      circle.style.top = `${desiredTop}px`;
      
      // 4) Position line - starts exactly at the bottom of the circle
      const circleHeight = circle.offsetHeight;
      const lineTop = desiredTop + circleHeight; // Line starts exactly at circle bottom
      line.style.top = `${lineTop}px`;

      // 5) Determine the height of the line (viewport coordinates only)
      const footer = document.querySelector('footer') as HTMLElement | null;

      // Bottom border: either viewport bottom edge or footer top edge – whichever is higher
      const footerTopViewport = footer ? footer.getBoundingClientRect().top : Number.POSITIVE_INFINITY;
      const bottomBoundary = Math.min(window.innerHeight, footerTopViewport);

      const height = Math.max(0, bottomBoundary - lineTop);
      line.style.height = `${height}px`;
    };

    // initial & Events
    onScrollOrResize();
    window.addEventListener('scroll', onScrollOrResize, { passive: true });
    window.addEventListener('resize', onScrollOrResize);
    return () => {
      window.removeEventListener('scroll', onScrollOrResize);
      window.removeEventListener('resize', onScrollOrResize);
    };
  }, [pathname]);

  return (
    <div className="scroll-rail-wrapper">
      <div ref={circleRef} className="scroll-rail-circle" />
      <div ref={lineRef} className="scroll-rail-line" />
    </div>
  );
};

export default ScrollRail;